import { useState } from 'react'
import { amendOrder, cancelOrder } from '../api'
import { clock, dolumOzeti, price as fmtPrice, qty } from '../format'
import { durumBilgisi } from '../orderStatus'
import { NewOrderForm } from './NewOrderForm'
import { OrderLogModal } from './OrderLogModal'
import type { Depth, ShadowOrder } from '../types'

/**
 * `Emirlerim` sekmesi — emir açma ve açık emirlerin yönetimi bir arada.
 *
 *   Yeni Teklif · emir girişi (yalnız AÇIK kontratta)
 *   liste       · [i] [yön] [tarih] [fiyat] [hacim 0/20] [düzenle] [sil]
 *
 * Emrin hayat döngüsünün tamamı burada: açılış, düzenleme, iptal, geçmiş.
 * `İşlemlerim` sekmesi yalnız gerçekleşen dolumları gösteriyor.
 *
 * `Tarih` emrin **ilk oluşma** anıdır; düzenleme onu değiştirmez (kimlik de
 * değişmez, yalnız sürüm artar). `Hacim` dolan/toplam — `20/20` tamamı eşleşti
 * demek.
 *
 * `i` ikonu emrin tüm hayatını gösteren modalı açar. `Sil` kaydı silmez,
 * durumu CANCELLED yapar; geçmiş kalıcı kayıtta durur.
 *
 * Düzenleme satır içinde: fiyat ve miktar kutuya dönüşür, onaylayınca sunucuya
 * PATCH gider. Ayrı bir modal açmak iki tıklama fazla.
 */

interface Props {
  orders: ShadowOrder[]
  contract: string | null
  closed: boolean
  depth: Depth | null
  /**
   * Emir giriş formu bu sekmenin başında gösterilsin mi. Mobilde `false`:
   * `TradePage` formu zaten ekranın üstünde tutuyor, ikincisi hem yer kaplar
   * hem de iki ayrı kutuya yazılan fiyat karışıklık yaratırdı.
   */
  formGoster?: boolean
}

export function OrdersTab({ orders, contract, closed, depth, formGoster = true }: Props) {
  const [duzenlenen, setDuzenlenen] = useState<string | null>(null)
  const [fiyat, setFiyat] = useState('')
  const [miktar, setMiktar] = useState('')
  const [logAcik, setLogAcik] = useState<string | null>(null)
  const [hata, setHata] = useState<string | null>(null)

  // Emirler dolduktan / iptal edildikten sonra da listede KALIR (kullanıcı
  // isteği): durum sütunu ne olduğunu söylüyor. Yeniden eskiye sıralı —
  // liste büyüdükçe son hareket üstte kalsın.
  const sirali = [...orders].sort((a, b) => b.createdAt - a.createdAt)

  function duzenlemeyeBasla(order: ShadowOrder) {
    setHata(null)
    setDuzenlenen(order.id)
    setFiyat(String(order.price))
    setMiktar(String(order.quantity))
  }

  async function kaydet(order: ShadowOrder) {
    setHata(null)
    try {
      await amendOrder(order.id, { price: Number(fiyat), quantity: Number(miktar) })
      setDuzenlenen(null)
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Emir düzenlenemedi')
    }
  }

  async function sil(order: ShadowOrder) {
    setHata(null)
    try {
      await cancelOrder(order.id)
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Emir iptal edilemedi')
    }
  }

  if (!contract) {
    return <div className="tab-empty">Emir girmek için bir kontrat seç.</div>
  }

  return (
    <div className="orders-tab">
      {/* Kapalı kontratta emir girişi yok — o kontrat bir daha işlem görmez.
          Açık emir listesi yine gösteriliyor: varsa görülüp iptal edilebilmeli. */}
      {formGoster && !closed && <NewOrderForm contract={contract} depth={depth} />}

      {hata && <div className="order-error" role="alert">{hata}</div>}

      <div className="orders-head">
        <span />
        <span>Yön</span>
        <span>Tarih</span>
        <span className="num">Fiyat</span>
        <span className="num">Hacim</span>
        <span />
        <span>Durum</span>
      </div>

      <div className="orders-body">
        {sirali.length === 0 ? (
          <div className="shadow-empty">Bu kontratta gölge emir yok.</div>
        ) : (
          sirali.map((order) => {
            const duzenleniyor = duzenlenen === order.id
            return (
              <div className="orders-row" key={order.id}>
                <button
                  type="button"
                  className="icon-btn info"
                  onClick={() => setLogAcik(order.id)}
                  title={`${order.id} · sürüm ${order.version} · geçmişi gör`}
                  aria-label={`${order.id} emrinin geçmişi`}
                >
                  ⓘ
                </button>

                <span
                  className={order.side === 'BUY' ? 'yon-al' : 'yon-sat'}
                  title={order.side === 'BUY' ? 'Alış' : 'Satış'}
                >
                  {order.side === 'BUY' ? '▲' : '▼'}
                </span>

                {/* İlk oluşma anı — düzenleme bunu değiştirmez. */}
                <span className="muted" title={`Emir kimliği: ${order.id}`}>
                  {clock(order.createdAt)}
                </span>

                {duzenleniyor ? (
                  <input
                    className="inline-input num"
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    min="0"
                    value={fiyat}
                    onChange={(e) => setFiyat(e.target.value)}
                    aria-label="Yeni fiyat"
                  />
                ) : (
                  <span className="num">{fmtPrice(order.price)}</span>
                )}

                {duzenleniyor ? (
                  <input
                    className="inline-input num"
                    type="number"
                    inputMode="numeric"
                    step="1"
                    min="0"
                    value={miktar}
                    onChange={(e) => setMiktar(e.target.value)}
                    aria-label="Yeni miktar (lot)"
                  />
                ) : (
                  <span
                    className="num"
                    // Kırılım tooltip'te: "6 Süpürdük · 4 Simüle" — bir emir
                    // birden çok yolla dolmuş olabilir.
                    title={
                      `${qty(order.filledQuantity)} lot doldu, ${qty(order.remaining)} lot kaldı` +
                      (dolumOzeti(order.fillBreakdown)
                        ? ` · ${dolumOzeti(order.fillBreakdown)}`
                        : '')
                    }
                  >
                    {qty(order.filledQuantity)}/{qty(order.quantity)}
                  </span>
                )}

                <span className="orders-actions">
                  {/* Kapanmış emirde eylem yok: dolmuş/iptal/süresi dolmuş bir
                      emir değiştirilemez (sunucu da 409 döndürür). Bilgi ikonu
                      satırın başında duruyor, geçmişe her durumda bakılabilir. */}
                  {order.status !== 'OPEN' ? null : duzenleniyor ? (
                    <>
                      <button
                        type="button"
                        className="icon-btn ok"
                        onClick={() => kaydet(order)}
                        title="Kaydet"
                        aria-label="Değişikliği kaydet"
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => setDuzenlenen(null)}
                        title="Vazgeç"
                        aria-label="Düzenlemeden vazgeç"
                      >
                        ×
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => duzenlemeyeBasla(order)}
                        title="Düzenle (kimlik değişmez, yeni sürüm oluşur)"
                        aria-label={`${order.id} emrini düzenle`}
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className="icon-btn danger"
                        onClick={() => sil(order)}
                        title="Sil (kayıt silinmez, emir iptal edilir)"
                        aria-label={`${order.id} emrini sil`}
                      >
                        🗑
                      </button>
                    </>
                  )}
                </span>

                {(() => {
                  const d = durumBilgisi(order.status)
                  return (
                    <span className={`durum-rozet ${d.tone}`} title={d.label}>
                      {d.code}
                    </span>
                  )
                })()}
              </div>
            )
          })
        )}
      </div>

      {logAcik && <OrderLogModal orderId={logAcik} onClose={() => setLogAcik(null)} />}
    </div>
  )
}
